import { useState, useEffect } from "react";
import { fetchGeneralNews } from "../lib/api_routes/news";

const ROWS_PER_PAGE = 20;

export const usePagination = (
  generalNews,
  formValues,
  accountInfo,
  bookmarksLoaded,
) => {
  const [newsCache, setNewsCache] = useState(new Map());
  const [currentItems, setCurrentItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [maxKnownPage, setMaxKnownPage] = useState(0);
  const [totalNews, setTotalNews] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Start news data when generalNews prop changes
  useEffect(() => {
    if (generalNews && bookmarksLoaded) {
      let newsArray = [];
      let count = 0;

      // Handle both direct and object with new_list formats
      if (Array.isArray(generalNews)) {
        newsArray = generalNews;
        count = generalNews.length;
      } else if (
        generalNews.news_list &&
        Array.isArray(generalNews.news_list)
      ) {
        newsArray = generalNews.news_list;
        count = generalNews.count || generalNews.news_list.length;

        // Set total news and pages if count is provided
        if (generalNews.count !== undefined) {
          setTotalNews(generalNews.count);
          setTotalPages(Math.ceil(generalNews.count / ROWS_PER_PAGE));
        }
      }
      if (newsArray.length > 0) {
        // Cache first page
        const cache = new Map();
        cache.set(0, newsArray);
        setNewsCache(cache);
        setCurrentItems(newsArray);
        setCurrentPage(0);
        setMaxKnownPage(0);

        // Determine if there are more pages
        const hasMore =
          count > 0
            ? newsArray.length === ROWS_PER_PAGE && count > newsArray.length
            : newsArray.length === ROWS_PER_PAGE;
        setHasMoreNews(hasMore);
      }
    }
  }, [generalNews, bookmarksLoaded]);

  // Fetch a specific page of news
  const fetchNewsPage = async (page) => {
    setIsLoading(true);
    try {
      // Calculate the offset -> how many news to skip before starting to return results
      const offset = page * ROWS_PER_PAGE;

      // Change API to include pagination parameters
      const newsResponse = await fetchGeneralNews({
        country: formValues.country,
        accountId: accountInfo?.id,
        offset,
        limit: ROWS_PER_PAGE,
        bookmarks_only: false,
      });

      let newsData = [];
      let count = totalNews;

      if (Array.isArray(newsResponse)) {
        newsData = newsResponse;
      } else if (
        newsResponse?.news_list &&
        Array.isArray(newsResponse.news_list)
      ) {
        // Object with news_list format
        newsData = newsResponse.news_list;
        if (newsResponse.count !== undefined) {
          count = newsResponse.count;
          setTotalNews(count);
          setTotalPages(Math.ceil(count / ROWS_PER_PAGE));
        }
      }

      if (newsData?.length > 0) {
        // Update cache with new data
        setNewsCache((prevCache) => {
          const updatedCache = new Map(prevCache);
          updatedCache.set(page, newsData);
          return updatedCache;
        });

        setCurrentItems(newsData);
        setMaxKnownPage(Math.max(maxKnownPage, page));

        // Update hasMoreNews based on total pages if available
        const totalLoadedItems = (page + 1) * ROWS_PER_PAGE;
        const hasMore =
          totalPages > 0
            ? totalLoadedItems < totalNews && newsData.length === ROWS_PER_PAGE
            : newsData.length === ROWS_PER_PAGE;

        setHasMoreNews(hasMore);
      } else {
        setHasMoreNews(false);
      }
    } catch (error) {
      console.error("Error fetching news page:", error);
      setHasMoreNews(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if we can navigate to next page
  const canGoToNextPage = () => {
    const nextPage = currentPage + 1;
    if (totalPages > 0) {
      return nextPage < totalPages;
    }
    return newsCache.has(nextPage) || hasMoreNews;
  };

  // Handle page navigation
  const handlePageChange = async (page) => {
    // Back to 0 - index
    const pageIndex = page - 1;
    if (pageIndex === currentPage) return;

    if (newsCache.has(pageIndex)) {
      setCurrentPage(pageIndex);
      setCurrentItems(newsCache.get(pageIndex));
    } else if (hasMoreNews && pageIndex === maxKnownPage + 1) {
      setCurrentPage(pageIndex);
      await fetchNewsPage(pageIndex);
    }
  };
  const handleNextPage = async () => {
    const nextPage = currentPage + 1;
    if (newsCache.has(nextPage)) {
      setCurrentPage(nextPage);
      setCurrentItems(newsCache.get(nextPage));
    } else if (hasMoreNews && !isLoading) {
      setCurrentPage(nextPage);
      await fetchNewsPage(nextPage);
    }
  };
  const handlePrevPage = () => {
    if (currentPage === 0) return;

    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    // Have always previous pages cached/stored
    if (newsCache.has(prevPage)) {
      setCurrentItems(newsCache.get(prevPage));
      // If we're going back, there should be a next page available (the one we just came from)
      if (!hasMoreNews && prevPage < maxKnownPage) {
        setHasMoreNews(true);
      }
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const current = currentPage + 1;
    // Calculate pages based on what's actually loaded
    const loadedPages = Math.max(maxKnownPage + 1, newsCache.size);

    // Show ellipsis for large page ranges
    if (currentPage >= 2) {
      pages.push(1);
      if (currentPage >= 3) {
        pages.push("...");
      }
    } else if (current > 1) {
      pages.push(1);
    }

    // Show 2 numbers in pagination -> [prev, current]
    const prev = current - 1;
    const next = current + 1;

    if (prev >= 1 && !pages.includes(prev)) {
      pages.push(prev);
    }
    // Add current page
    pages.push(current);
    // Add next page (if it exists)
    if (next <= loadedPages) {
      pages.push(next);
    }
    return pages;
  };

  return {
    newsCache,
    currentItems,
    setCurrentItems,
    currentPage,
    isLoading,
    hasMoreNews,
    canGoToNextPage,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    getPageNumbers,
    totalNews,
    totalPages,
    rowsPerPage: ROWS_PER_PAGE,
  };
};
